import { Box, Typography } from "@mui/material";
import { palette, spacing } from "../../styles/theme";
import { useTranslation } from "react-i18next";

interface ProjectCardDragPreviewProps {
  projectName: string;
  designs?: Array<{
    id: string;
    name: string;
    images?: Array<{
      url: string;
      side: string;
    }>;
  }>;
}

export const ProjectCardDragPreview = ({
  projectName,
  designs = [],
}: ProjectCardDragPreviewProps) => {
  const { t } = useTranslation();
  const NUMBER_OF_DESIGN_PER_CARD = 3;
  const numberOfBlankDesign =
    NUMBER_OF_DESIGN_PER_CARD - Math.min(designs.length, 3);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: palette.grey.lightBackground,
        border: `1px solid ${palette.blue.secondaryEffect}`,
        borderRadius: "12px",
        boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.3)",
        minWidth: "276px",
        maxWidth: "276px",
        padding: "16px",
        cursor: "grabbing",
        opacity: 0.5,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gridTemplateRows: "repeat(2, 1fr)",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        {designs.map((design) => (
          <Box
            key={design.id}
            sx={{
              flexBasis: "calc(50% - 4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              minHeight: "90px",
              maxHeight: "90px",
              minWidth: "120px",
              padding: "8px",
              backgroundColor: palette.white,
              borderRadius: "8px",
              userSelect: "none",
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                gap: spacing.smallDesktop,
                py: "5px",
              }}
            >
              {design?.images?.map((image) => (
                <Box
                  component={"img"}
                  draggable={false}
                  sx={{
                    maxHeight: "49px",
                    userSelect: "none",
                    content: `url(${image.url})`,
                  }}
                  key={image.url}
                />
              ))}
            </Box>
          </Box>
        ))}

        <Box
          sx={{
            flexBasis: "calc(50% - 4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "90px",
            maxHeight: "90px",
            minWidth: "120px",
            backgroundColor: palette.white,
            borderRadius: "8px",
          }}
        >
          <Typography variant="textSm" color={palette.blue.secondary}>
            {t(designs.length >= 1 ? "project.viewAll" : "project.openProject")}
          </Typography>
        </Box>

        {Array(...new Array(numberOfBlankDesign)).map((_, index) => (
          <Box
            key={`${index}-blank`}
            sx={{
              flexBasis: "calc(50% - 4px)",
              minHeight: "90px",
              height: "90px",
              minWidth: "120px",
              padding: "8px",
              userSelect: "none",
            }}
          />
        ))}
      </Box>

      <Box display="flex" flexDirection="column" gap="5px">
        <Typography
          variant="textRegular"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: palette.grey.nearBlack,
          }}
        >
          {projectName}
        </Typography>
      </Box>
    </Box>
  );
};
